import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import DataCard from './data-card/data-card';
import TruncatedTextWithAutoTooltip from '../../../../../../../shared/components/truncated-text-with-auto-tooltip';
import { ProspectSequenceNameProps } from './types';

const ProspectSequenceName = ({
  sequences,
  allOutcomes,
  isSubsequence = false,
  showForKanbanView = false,
  selectedProspectId,
}: ProspectSequenceNameProps) => {
  const { t } = useTranslation();

  const [isVisibleAllSequences, setIsVisibleAllSequences] = useState<boolean>(
    false,
  );

  const mappedCell = sequences.length
    ? sequences.map((sequence) => ({
        sequenceId: sequence.id,
        sequence: sequence.title,
        status: sequence.status,
        prospectId: sequence.prospectId,
        categoryId: sequence.categoryId,
        parentSequenceId: sequence.parentSequenceId,
      }))
    : [];

  const cls = classNames([
    'regular-2 sequence-name font-medium blue-txt-11',
    {
      'underline-txt': isVisibleAllSequences,
    },
  ]);

  const onToggle = () => {
    setIsVisibleAllSequences(!isVisibleAllSequences);
  };

  const showAllSequences = () => {
    setIsVisibleAllSequences(true);
  };

  // Compact view: show first sequence name + "+N" for multiple sequences
  const renderCompactView = () => {
    if (mappedCell.length === 0) {
      return '-';
    }

    if (mappedCell.length === 1) {
      // Single sequence: just show the name (no popover)
      const singleSequence = mappedCell[0];
      return (
        <Link
          to={
            isSubsequence
              ? `/sequence/${singleSequence.parentSequenceId}/subsequence/${singleSequence.sequenceId}/conditions`
              : `/sequence/${singleSequence.sequenceId}/steps`
          }
          className="regular-2 font-medium blue-txt-11 text-decoration-none d-flex"
        >
          <TruncatedTextWithAutoTooltip
            text={singleSequence.sequence}
            className="kanban-sequence-name"
          />
        </Link>
      );
    }

    return (
      <>
        {/* OverlayPopover is disabled for Kanban view to avoid opening it on click. */}
        <OverlayPopover
          show={isVisibleAllSequences}
          onToggle={onToggle}
          className="sequence-name-popover active-sequences-popover"
          content={
            <div className="active-sequences-popover-content">
              <h2>{isSubsequence ? 'Subsequences' : t('labels.sequences')}</h2>
              <div className="active-sequences-wrapper">
                {mappedCell.map((sequence) => (
                  <Link
                    key={sequence.sequenceId}
                    className="active-sequence text-decoration-none"
                    to={
                      isSubsequence
                        ? `/sequence/${sequence.parentSequenceId}/subsequence/${sequence.sequenceId}/conditions`
                        : `/sequence/${sequence.sequenceId}/steps`
                    }
                  >
                    <p>{sequence.sequence}</p>
                  </Link>
                ))}
              </div>
            </div>
          }
          placement={Placement.Auto}
          rootClose
        >
          <span
            role="button"
            {...accessibleOnClick(showAllSequences)}
            className={`${cls} d-flex`}
          >
            <OverlayTooltip text="Associated Sequences">
              <span>{mappedCell?.length} Sequences</span>
            </OverlayTooltip>
          </span>
        </OverlayPopover>
      </>
    );
  };

  const renderOverlayPopoverSpan = (
    <OverlayPopover
      show={isVisibleAllSequences}
      onToggle={onToggle}
      className="sequence-name-popover"
      content={
        <DataCard
          data={mappedCell}
          showAllSequences={onToggle}
          allOutcomes={allOutcomes}
          isSubsequence={isSubsequence}
          selectedProspectId={selectedProspectId}
        />
      }
      placement={Placement.Auto}
      rootClose
    >
      <span
        role="button"
        {...accessibleOnClick(showAllSequences)}
        className={cls}
      >
        {`${mappedCell.length} ${
          isSubsequence ? 'Subsequences' : t('labels.sequences')
        }`}
      </span>
    </OverlayPopover>
  );

  const renderSpan = () => {
    // Use slice to avoid mutating the original array
    const firstElement = mappedCell.length > 0 ? mappedCell[0] : null;

    if (firstElement) {
      return (
        <OverlayPopover
          show={isVisibleAllSequences}
          onToggle={onToggle}
          className="sequence-name-popover"
          content={
            <DataCard
              data={[firstElement]}
              showAllSequences={onToggle}
              allOutcomes={allOutcomes}
              isSubsequence={isSubsequence}
              selectedProspectId={selectedProspectId}
            />
          }
          placement={Placement.Auto}
          rootClose
        >
          <span
            role="button"
            {...accessibleOnClick(showAllSequences)}
            className="regular-2 sequence-name font-medium text-decoration-none blue-txt-11"
          >
            {firstElement.sequence}
          </span>
        </OverlayPopover>
      );
    }

    return '-';
  };

  // Use compact view if prop is true
  if (showForKanbanView) {
    return <div className="sequence-name-wrap">{renderCompactView()}</div>;
  }

  // Default behavior (existing logic)
  return (
    <div className="sequence-name-wrap">
      {mappedCell?.length > 1 ? renderOverlayPopoverSpan : renderSpan()}
    </div>
  );
};

export default ProspectSequenceName;
